import React from 'react';
import { ModelStatsMap } from '../../../types/analytics.types';
import { MODEL_KEYS, MODEL_LABELS } from '../../../config/analytics';
import { fmt, formatCurrency } from '../../../utils/formatters';

interface Props {
  statsMap: ModelStatsMap;
  selectedModel: string | null;
  onSelectModel: (modelKey: string) => void;
  modelSizes: Record<string, number>;
}

const ModelPerformanceTable: React.FC<Props> = ({ statsMap, selectedModel, onSelectModel, modelSizes }) => {
  const activeModels = MODEL_KEYS.filter(k => statsMap[k] && statsMap[k].totalTrades > 0);

  if (activeModels.length === 0) {
    return (
      <div className="analytics-section">
        <h3 className="analytics-section-title">Model Performance</h3>
        <p className="analytics-empty">No model trades found.</p>
      </div>
    );
  }

  const totalPL = activeModels.reduce((sum, k) => sum + (statsMap[k]?.totalPL || 0), 0);
  const totalWins = activeModels.reduce((sum, k) => sum + (statsMap[k]?.wins || 0), 0);
  const totalLosses = activeModels.reduce((sum, k) => sum + (statsMap[k]?.losses || 0), 0);
  const totalTrades = activeModels.reduce((sum, k) => sum + (statsMap[k]?.totalTrades || 0), 0);
  const totalGrossWins = activeModels.reduce((sum, k) => sum + (statsMap[k]?.totalWinAmount || 0), 0);
  const totalGrossLosses = activeModels.reduce((sum, k) => sum + (statsMap[k]?.totalLossAmount || 0), 0);

  return (
    <div className="analytics-section">
      <h3 className="analytics-section-title">Model Performance</h3>
      <div className="analytics-table-wrapper">
        <table className="analytics-table">
          <thead>
            <tr>
              <th className="text-left">Model</th>
              <th className="text-center hide-mobile">Size</th>
              <th className="text-center">Record</th>
              <th className="text-center hide-mobile">Win Rate</th>
              <th className="text-center hide-mobile">PF</th>
              <th className="text-right hide-mobile">Gross Wins</th>
              <th className="text-right hide-mobile">Gross Losses</th>
              <th className="text-right">Net P/L</th>
            </tr>
          </thead>
          <tbody>
            {activeModels.map(k => {
              const s = statsMap[k];
              const isSelected = selectedModel === k;
              return (
                <tr
                  key={k}
                  className={`clickable-row ${isSelected ? 'selected' : ''}`}
                  onClick={() => onSelectModel(k)}
                >
                  <td className="text-left model-name">{MODEL_LABELS[k] || k}</td>
                  <td className="text-center hide-mobile">{modelSizes[k] || '-'}</td>
                  <td className="text-center nowrap">{s.wins}W/{s.losses}L</td>
                  <td className={`text-center hide-mobile ${s.winRate >= 50 ? 'positive' : 'negative'}`}>
                    {fmt(s.winRate, 1)}%
                  </td>
                  <td className={`text-center hide-mobile ${s.profitFactor >= 1 ? 'positive' : 'negative'}`}>
                    {fmt(s.profitFactor)}
                  </td>
                  <td className="text-right nowrap hide-mobile positive">
                    {formatCurrency(s.totalWinAmount)}
                  </td>
                  <td className="text-right nowrap hide-mobile negative">
                    {formatCurrency(s.totalLossAmount)}
                  </td>
                  <td className={`text-right nowrap font-bold ${s.totalPL >= 0 ? 'positive' : 'negative'}`}>
                    {formatCurrency(s.totalPL)}
                  </td>
                </tr>
              );
            })}
            <tr className="total-row">
              <td className="text-left">TOTAL</td>
              <td className="text-center hide-mobile"></td>
              <td className="text-center nowrap">{totalWins}W/{totalLosses}L</td>
              <td className={`text-center hide-mobile ${totalTrades > 0 && (totalWins / totalTrades) * 100 >= 50 ? 'positive' : 'negative'}`}>
                {totalTrades > 0 ? fmt((totalWins / totalTrades) * 100, 1) : '0.0'}%
              </td>
              <td className="text-center hide-mobile">-</td>
              <td className="text-right nowrap hide-mobile positive">
                {formatCurrency(totalGrossWins)}
              </td>
              <td className="text-right nowrap hide-mobile negative">
                {formatCurrency(totalGrossLosses)}
              </td>
              <td className={`text-right nowrap font-bold ${totalPL >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(totalPL)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ModelPerformanceTable;
